<template>
  <div class="rb-table-card">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div v-if="!hideDate">
        <DateRange compact />
      </div>
      <div v-else></div>
      <el-button
          v-if="tableData && tableData.length > 0"
          size="small"
          plain
          @click="openExportDialog"
      >
        <i class="fas fa-download me-1"></i>{{ $t('ReportBuilder.table.exportCsv') }}
      </el-button>
    </div>
    <HardcodedDateWarning v-if="!hideDate" />
    <div v-if="tableData && tableData.length > 0" class="flex-grow-1 overflow-y-auto">
      <TableHelper
          :tableData="tableData"
          :columns="columns"
          :enableSorting="true"
          :initialSort="initialSort"
          @sort-change="onSortChange"
      />
    </div>
    <div v-if="tableData && tableData.length > 0" class="d-flex justify-content-end mt-3">
      <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100, 500]"
          small
          background
          layout="total, sizes, prev, pager, next"
          :total="totalCount"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
      />
    </div>
    <div v-else class="rb-empty-state">
      <i class="fas fa-table rb-empty-icon"></i>
      <p>{{ $t('ReportBuilder.table.noData') }}</p>
    </div>

    <!-- CSV Export Dialog -->
    <el-dialog
        v-model="exportDialogVisible"
        :title="$t('ReportBuilder.table.exportCsvTitle')"
        width="420px"
        :close-on-click-modal="true"
    >
      <div class="export-form">
        <div class="mb-3">
          <label class="rb-label">{{ $t('ReportBuilder.table.exportLimit') }}</label>
          <el-input-number
              v-model="exportLimit"
              :min="1"
              :max="100000"
              :step="1000"
              style="width: 100%"
          />
          <div class="export-hint mt-1">{{ $t('ReportBuilder.table.exportLimitHint') }}</div>
        </div>
        <div class="mb-3">
          <label class="rb-label">{{ $t('ReportBuilder.table.exportOffset') }}</label>
          <el-input-number
              v-model="exportOffset"
              :min="0"
              :step="1000"
              style="width: 100%"
          />
        </div>
      </div>
      <template #footer>
        <el-button @click="exportDialogVisible = false">{{ $t('ReportBuilder.table.exportCancel') }}</el-button>
        <el-button type="primary" :loading="exportLoading" @click="runExport">
          <i class="fas fa-download me-1"></i>{{ $t('ReportBuilder.table.exportRun') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import TableHelper from "@/components/Table.vue"
import DateRange from "./_dateRange.vue"
import HardcodedDateWarning from "./_hardcodedDateWarning.vue"
import ReportBuilderService from "@/helpers/api/reportBuilder.service"

export default {
  name: "_table_data",
  components: { TableHelper, DateRange, HardcodedDateWarning },
  props: {
    hideDate: {
      type: Boolean,
      default: false
    }
  },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    }
  },
  data() {
    return {
      exportDialogVisible: false,
      exportLimit: 10000,
      exportOffset: 0,
      exportLoading: false,
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    tableData() {
      return this.reportData.chartData || []
    },
    columns() {
      const sourceCols = this.reportData.source_request.columns || []
      const firstRow = this.tableData[0] || {}

      return sourceCols.map(colName => {
        let prop = colName
        if (firstRow[prop] === undefined && colName.includes('.')) {
          const parts = colName.split('.')
          const lastPart = parts[parts.length - 1]
          if (firstRow[lastPart] !== undefined) {
            prop = lastPart
          }
        }

        // Build i18n key: strip leading "events." then replace dots with underscores
        const stripped = colName.replace(/^events\./, '')
        const i18nKey = 'ReportBuilder.constructor.columns.' + stripped.replace(/\./g, '_')
        const label = this.$te(i18nKey) ? this.$t(i18nKey) : (prop.split('.').pop() || prop)

        return {
          prop: prop,
          label: label,
          sortable: true,
          reduction: false
        }
      })
    },
    initialSort() {
      const order = this.reportData.source_request.order || {}
      const originalProp = Object.keys(order)[0]
      if (originalProp) {
        let normalizedProp = originalProp
        if (originalProp.includes('.')) {
          normalizedProp = originalProp.split('.').pop()
        }
        return {
          prop: normalizedProp,
          direction: order[originalProp]
        }
      }
      return null
    },
    totalCount() {
      return this.reportData.total_count || 0
    },
    pageSize() {
      return this.reportData.source_request.limit || 50
    },
    currentPage() {
      return Math.floor((this.reportData.source_request.offset || 0) / this.pageSize) + 1
    }
  },
  methods: {
    onSortChange(sort) {
      const prop = Object.keys(sort)[0]
      if (!prop) {
        this.$store.commit('ReportFromBuilder/setListOrder', {})
      } else {
        const direction = sort[prop]
        const sourceCols = this.reportData.source_request.columns || []
        const originalCol = sourceCols.find(c => c === prop || c.endsWith('.' + prop))

        const payload = {}
        payload[originalCol || prop] = direction
        this.$store.commit('ReportFromBuilder/setListOrder', payload)
      }
      this.$store.dispatch('ReportFromBuilder/loadDataForReport')
    },
    handleSizeChange(val) {
      this.$store.commit('ReportFromBuilder/setListLimit', val)
      this.$store.commit('ReportFromBuilder/setListOffset', 0)
      this.$store.dispatch('ReportFromBuilder/loadDataForReport')
    },
    handleCurrentChange(val) {
      const offset = (val - 1) * this.pageSize
      this.$store.commit('ReportFromBuilder/setListOffset', offset)
      this.$store.dispatch('ReportFromBuilder/loadDataForReport')
    },
    openExportDialog() {
      this.exportOffset = 0
      this.exportDialogVisible = true
    },
    async runExport() {
      this.exportLoading = true
      try {
        const sourceRequest = JSON.parse(JSON.stringify(this.reportData.source_request))
        sourceRequest.limit = this.exportLimit
        sourceRequest.offset = this.exportOffset

        const settings = JSON.parse(JSON.stringify(this.reportData.settings || {}))
        // Resolve $current_resource placeholder (same logic as store's loadDataForReport)
        if (settings?.params?.[':resource_key']) {
          const currentBotKey = this.$store.state.init?.current_bot?.key
          settings.params[':resource_key'] = settings.params[':resource_key']
            .map(key => key === '$current_resource' ? currentBotKey : key)
            .filter(Boolean)
        }

        const payload = { source_request: sourceRequest, settings }
        await ReportBuilderService.exportCsvReport({}, payload)
        this.exportDialogVisible = false
      } catch (e) {
        this.$message?.error(this.$t('ReportBuilder.table.exportError'))
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>

<style scoped>
.rb-table-card {
  background: var(--ui-bg-card, #fff);
  border: 1px solid var(--ui-border, #e8e8e8);
  border-radius: 12px;
  padding: 20px;
  margin-top: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.rb-empty-state {
  text-align: center;
  padding: 48px 20px;
  color: var(--ui-text-3, #909399);
}

.rb-empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.rb-empty-state p {
  font-size: 15px;
  margin: 0;
  font-weight: 500;
}

.export-form .rb-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ui-text-2, #606266);
  margin-bottom: 6px;
}

.export-hint {
  font-size: 11px;
  color: var(--ui-text-3, #909399);
}
</style>
